// ABOUTME: A 6-digit OTP input with individual boxes for each digit.
// ABOUTME: Used for TOTP code entry during MFA login and setup flows.
import { Box, useTheme } from "@mui/material";
import { useRef, useEffect } from "react";
import { tokens } from "../../../theme";

const DIGIT_COUNT = 6;

const OtpInput = ({ value = "", onChange, onComplete, error, autoFocus, disabled }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);

    const getDigit = (index) => {
        return value[index] || "";
    };

    const focusInput = (index) => {
        const clamped = Math.max(0, Math.min(index, DIGIT_COUNT - 1));
        if (inputRefs.current[clamped]) {
            inputRefs.current[clamped].focus();
            inputRefs.current[clamped].select();
        }
    };

    const setValueFromDigits = (newDigits) => {
        const joined = newDigits.join("");
        onChange(joined);
        if (joined.length === DIGIT_COUNT && onComplete) {
            onComplete(joined);
        }
    };

    const handleChange = (index, e) => {
        const input = e.target.value.replace(/\D/g, "");
        if (!input) return;

        // Handle paste or autofill of multiple digits
        if (input.length > 1) {
            const pasted = input.slice(0, DIGIT_COUNT);
            onChange(pasted);
            if (pasted.length === DIGIT_COUNT && onComplete) {
                onComplete(pasted);
            }
            focusInput(Math.min(pasted.length, DIGIT_COUNT - 1));
            return;
        }

        const newDigits = Array.from({ length: DIGIT_COUNT }, (_, i) => getDigit(i));
        newDigits[index] = input[0];
        setValueFromDigits(newDigits);

        if (index < DIGIT_COUNT - 1) {
            focusInput(index + 1);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            const newDigits = Array.from({ length: DIGIT_COUNT }, (_, i) => getDigit(i));
            if (newDigits[index]) {
                newDigits[index] = "";
                setValueFromDigits(newDigits);
            } else if (index > 0) {
                newDigits[index - 1] = "";
                setValueFromDigits(newDigits);
                focusInput(index - 1);
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            focusInput(index - 1);
        } else if (e.key === "ArrowRight" && index < DIGIT_COUNT - 1) {
            focusInput(index + 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGIT_COUNT);
        if (pasted) {
            onChange(pasted);
            focusInput(Math.min(pasted.length, DIGIT_COUNT - 1));
        }
    };

    const boxSx = {
        width: 48,
        height: 56,
        border: `2px solid ${error ? (colors.red?.[400]) : colors.grey[200]}`,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.mode === "dark" ? colors.grey[300] : "#fff",
        flexShrink: 0,
        "& input": {
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            textAlign: "center",
            fontSize: "24px",
            fontWeight: 600,
            background: "transparent",
            color: colors.txt["primary"],
            caretColor: "transparent",
            padding: 0,
        },
        "&:focus-within": {
            borderColor: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
        },
    };

    return (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
            {Array.from({ length: DIGIT_COUNT }, (_, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {index === 3 && <Box sx={{ width: 8 }} />}
                    <Box sx={boxSx}>
                        <input
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={getDigit(index)}
                            onChange={(e) => handleChange(index, e)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            onFocus={(e) => e.target.select()}
                            disabled={disabled}
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default OtpInput;
